import { useEffect, useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { heroMessages, heroSideLabels, site } from "../content";
import { scrollToId } from "../lib/scroll";

const MESSAGE_RANGES = [
  [0.0, 0.02, 0.13, 0.17],
  [0.2, 0.24, 0.34, 0.38],
  [0.42, 0.46, 0.56, 0.6],
  [0.62, 0.66, 0.76, 0.8],
];

const HeroCtas = () => (
  <div className="mt-8 flex flex-wrap items-center gap-3">
    <button
      type="button"
      data-testid="hero-cta-work"
      onClick={() => scrollToId("projetos")}
      className="group flex items-center gap-3 rounded-full bg-white py-2 pl-6 pr-2 shadow-2xl transition-colors duration-300 hover:bg-[#00E5FF]"
    >
      <span className="font-display text-xs font-bold uppercase tracking-wide text-black">Conheça meu trabalho</span>
      <span className="grid h-8 w-8 place-items-center rounded-full bg-black text-white transition-transform duration-300 group-hover:translate-y-0.5">
        <ArrowDown size={14} />
      </span>
    </button>
    <button
      type="button"
      data-testid="hero-cta-contact"
      onClick={() => scrollToId("contato")}
      className="group flex items-center gap-3 rounded-full border border-white/30 py-2 pl-6 pr-2 text-white backdrop-blur transition-colors duration-300 hover:border-[#00E5FF] hover:text-[#00E5FF]"
    >
      <span className="font-display text-xs font-bold uppercase tracking-wide">Vamos criar algo juntos</span>
      <span className="grid h-8 w-8 place-items-center rounded-full border border-current">
        <ArrowUpRight size={14} />
      </span>
    </button>
  </div>
);

const IdentityPill = ({ progress }) => {
  const opacity = useTransform(progress, [0, 0.02, 0.78, 0.84], [0, 1, 1, 0]);
  return (
    <motion.span
      style={{ opacity }}
      data-testid="hero-identity-pill"
      className="absolute left-6 top-[24%] inline-flex items-center rounded-full border border-white/20 bg-white/10 px-3.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.22em] text-white/90 backdrop-blur md:left-16 md:top-[26%] lg:left-24 md:text-[10px]"
    >
      Lucas Rocha · Publicitário
    </motion.span>
  );
};

const HeroMessage = ({ progress, range, message, revealOnLoad }) => {
  const [a, b, c, d] = range;
  const opacity = useTransform(progress, [a, b, c, d], revealOnLoad ? [1, 1, 1, 0] : [0, 1, 1, 0]);
  const y = useTransform(progress, [a, b, c, d], revealOnLoad ? [0, 0, 0, -30] : [30, 0, 0, -30]);
  const filter = useTransform(
    progress,
    [a, b, c, d],
    revealOnLoad ? ["blur(0px)", "blur(0px)", "blur(0px)", "blur(8px)"] : ["blur(8px)", "blur(0px)", "blur(0px)", "blur(8px)"]
  );
  return (
    <motion.div
      style={{ opacity, y, filter }}
      data-testid={`hero-${message.id}`}
      className="absolute inset-x-6 top-[34%] md:inset-x-auto md:left-16 md:top-[36%] md:max-w-2xl lg:left-24 lg:max-w-3xl"
    >
      <p className="font-display text-4xl font-extrabold leading-[1.02] tracking-tighter text-white drop-shadow-[0_2px_24px_rgba(0,0,0,0.45)] sm:text-5xl lg:text-7xl">
        {message.lines.map((line, i) => (
          <span key={i} className="block overflow-hidden pb-1">
            <motion.span
              className="block"
              initial={revealOnLoad ? { y: "112%" } : false}
              animate={revealOnLoad ? { y: "0%" } : undefined}
              transition={{ duration: 0.9, delay: 0.35 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
            >
              {line}
            </motion.span>
          </span>
        ))}
      </p>
    </motion.div>
  );
};

const HeroFinal = ({ progress }) => {
  const opacity = useTransform(progress, [0.84, 0.93], [0, 1]);
  const y = useTransform(progress, [0.84, 0.93], [44, 0]);
  return (
    <motion.div
      style={{ opacity, y }}
      data-testid="hero-final"
      className="pointer-events-none absolute inset-x-6 top-[26%] md:left-16 md:top-[27%] lg:left-24 md:right-auto md:max-w-3xl"
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] md:text-xs">Publicitário</p>
      <h1 className="mt-3 font-display text-6xl font-black leading-[0.9] tracking-tighter text-white drop-shadow-[0_2px_30px_rgba(0,0,0,0.5)] sm:text-7xl lg:text-9xl">
        Lucas
        <br />
        Rocha
      </h1>
      <div className="pointer-events-auto">
        <HeroCtas />
      </div>
    </motion.div>
  );
};

const StaticHero = () => (
  <section
    id="hero"
    className="relative flex min-h-screen items-center overflow-hidden bg-[#050D16]"
    data-testid="hero-section"
    aria-label="Apresentação de Lucas Rocha"
  >
    <img
      src="/assets/hero-poster.jpg"
      alt=""
      className="absolute inset-0 h-full w-full object-cover"
      style={{ objectPosition: "center 35%" }}
    />
    <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/35 to-transparent" />
    <div className="relative px-6 py-32 md:px-16 lg:px-24">
      <span className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-white/90 backdrop-blur">
        Lucas Rocha · Publicitário
      </span>
      <div className="mt-10 space-y-3">
        {heroMessages.map((m) => (
          <p key={m.id} className="font-display text-xl font-bold tracking-tight text-white/85 md:text-2xl">
            {m.lines.join(" ")}
          </p>
        ))}
      </div>
      <h1 className="mt-12 font-display text-6xl font-black leading-[0.9] tracking-tighter text-white md:text-8xl">
        Lucas
        <br />
        Rocha
      </h1>
      <p className="mt-3 font-mono text-xs uppercase tracking-[0.3em] text-[#00E5FF]">Publicitário</p>
      <HeroCtas />
    </div>
  </section>
);

export const Hero = () => {
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const timeRef = useRef(null);
  const progressRef = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end end"] });
  const hintOpacity = useTransform(scrollYProgress, [0.8, 0.9], [1, 0]);

  useEffect(() => {
    if (reduced) return undefined;
    const video = videoRef.current;
    if (!video) return undefined;
    let cancelled = false;
    let objectUrl = null;
    const directUrl = video.canPlayType('video/webm; codecs="vp9"') ? "/assets/hero-web.webm" : "/assets/hero-web.mp4";
    const fallbackToDirect = () => {
      if (!cancelled) {
        video.src = directUrl;
        video.load();
      }
    };
    const onError = () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
      fallbackToDirect();
    };
    video.addEventListener("error", onError);
    (async () => {
      try {
        const res = await fetch(directUrl);
        if (!res.ok) throw new Error("video fetch failed");
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        video.src = objectUrl;
        video.load();
      } catch (e) {
        fallbackToDirect();
      }
    })();
    return () => {
      cancelled = true;
      video.removeEventListener("error", onError);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [reduced]);

  useEffect(() => {
    if (reduced) return undefined;
    const video = videoRef.current;
    if (!video) return undefined;
    let raf;
    let current = 0;
    const tick = () => {
      const p = Math.min(Math.max(scrollYProgress.get(), 0), 1);
      const d = video.duration && !Number.isNaN(video.duration) ? video.duration : 8;
      const target = p * Math.max(d - 0.06, 0);
      current += (target - current) * 0.16;
      if (Math.abs(target - current) < 0.004) current = target;
      if (video.readyState >= 2 && Math.abs(video.currentTime - current) > 0.03) {
        try {
          video.currentTime = current;
        } catch (e) {
          /* seek race — ignored */
        }
      }
      if (timeRef.current) timeRef.current.textContent = current.toFixed(2);
      if (progressRef.current) progressRef.current.style.transform = `scaleX(${p})`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [reduced, scrollYProgress]);

  if (reduced) return <StaticHero />;

  return (
    <section id="hero" ref={sectionRef} className="relative h-[520vh]" data-testid="hero-section" aria-label="Apresentação de Lucas Rocha">
      <div className="sticky top-0 h-screen w-full overflow-hidden bg-[#050D16]">
        <video
          ref={videoRef}
          className="absolute inset-0 h-full w-full object-cover"
          style={{ objectPosition: "center 35%" }}
          poster="/assets/hero-poster.jpg"
          muted
          playsInline
          preload="auto"
          disablePictureInPicture
          aria-hidden="true"
          tabIndex={-1}
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-black/55 via-black/15 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/45 to-transparent" />

        <div className="absolute left-4 top-[4.75rem] md:left-8 md:top-24">
          <span
            data-testid="hero-timecode"
            className="inline-flex items-center rounded-md border border-white/15 bg-black/55 px-2 py-1 font-mono text-[11px] text-white/90 backdrop-blur"
          >
            <span ref={timeRef}>0.00</span>s
          </span>
        </div>

        <div
          className="absolute bottom-24 right-8 top-28 hidden w-60 flex-col justify-between text-right lg:flex"
          data-testid="hero-side-labels"
        >
          <div className="space-y-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/55">
            <p>{heroSideLabels.topics}</p>
            <p className="pt-3">{heroSideLabels.availability}</p>
          </div>
          <div className="space-y-8">
            <div>
              <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-white/40">{heroSideLabels.nowLabel}</p>
              <p className="mt-1.5 text-sm font-medium leading-snug text-white/85">{heroSideLabels.nowText}</p>
            </div>
            <div>
              <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-white/40">{heroSideLabels.focusLabel}</p>
              <ul className="mt-1.5 space-y-1 text-sm text-white/70">
                {heroSideLabels.focusItems.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-white/40">{heroSideLabels.contactLabel}</p>
              <a href={`mailto:${site.email}`} className="mt-1.5 block font-mono text-[11px] text-[#00E5FF] hover:underline">
                {site.email}
              </a>
            </div>
          </div>
        </div>

        <div className="pointer-events-none absolute inset-0">
          <IdentityPill progress={scrollYProgress} />
          {heroMessages.map((m, i) => (
            <HeroMessage key={m.id} progress={scrollYProgress} range={MESSAGE_RANGES[i]} message={m} revealOnLoad={i === 0} />
          ))}
          <HeroFinal progress={scrollYProgress} />
        </div>

        <motion.div
          style={{ opacity: hintOpacity }}
          data-testid="hero-scroll-hint"
          className="absolute bottom-10 left-6 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-white/60 md:left-16 lg:left-24"
        >
          <ArrowDown size={12} className="animate-bounce" />
          Role para explorar
        </motion.div>

        <div className="absolute bottom-0 left-0 h-[3px] w-full bg-white/10" data-testid="hero-progress">
          <div ref={progressRef} className="h-full w-full origin-left bg-[#00E5FF]" style={{ transform: "scaleX(0)" }} />
        </div>
      </div>
    </section>
  );
};
